import { CSSInterpolation } from '@emotion/css/create-instance'
import React, { type ReactNode } from 'react'

import { CssVars } from '../../themes'
import { getStylesForTextType, Type } from '../Type'

/**
 * This component is meant to be used internally to properly lay out checkbox and
 * radio button inputs with their text labels.
 */
export function InternalCheckboxOrRadioButtonRow({
  _experimentalVariant = 'default',
  input,
  inputHeight,
  label,
  overrideRootStyles,
}: {
  /**
   * Label text rendered next to the passed in input
   */
  label: string

  /**
   * The input element. Must be either a RadioButton or a Checkbox.
   */
  input: ReactNode

  /**
   * The height, in pixels, of the passed in input element
   */
  inputHeight: number

  overrideRootStyles?: CSSInterpolation

  _experimentalVariant?: 'default' | 'legacy'
}) {
  const labelLineHeight = getStylesForTextType({ size: 'S' }).lineHeight
  return (
    <label
      css={[
        {
          alignItems: 'flex-start',
          display: 'flex',
        },
        _experimentalVariant === 'default' && {
          '&:not(:last-of-type)': {
            marginBottom: CssVars.size.gutterWidthSm,
          },
        },
        _experimentalVariant === 'legacy' && {
          borderBottom: `1px solid ${CssVars.neutral.bg3}`,
          paddingBottom: 17,
          paddingTop: 18,
        },
        overrideRootStyles,
      ]}
    >
      <div
        css={{
          // NOTE(jare): Set display to flex to remove any extra space this div would
          // otherwise render around the checkbox
          display: 'flex',
          marginRight:
            _experimentalVariant === 'default' ? CssVars.size.gutterWidth : 18,
          // NOTE(jare): Push the checkbox down so that it's in the center of the
          // first line of the label's text in both the case where the label has
          // one line of text and the case where the label has multiple lines of
          // text
          paddingTop: `calc((${labelLineHeight} - ${inputHeight}px) / 2)`,
        }}
      >
        {input}
      </div>
      <Type.Text size="S">{label}</Type.Text>
    </label>
  )
}
